import { useCallback, useEffect, useMemo } from "react";
import { mediaCache } from "@/utils/mediaCache";

export type MediaStatus = "idle" | "ready";

export interface UseMediaResult {
  /** "idle" when there is no URL, otherwise "ready" (a src is always available). */
  status: MediaStatus;
  /**
   * The URL to render. This is the in-memory `blob:` object URL when the file is
   * already cached, otherwise the original network URL (which is rendered while
   * the cache warms up in the background so there is never an extra delay).
   */
  src: string | null;
  /** True when `src` is served from the in-memory cache (no network involved). */
  isCached: boolean;
  /**
   * The original network URL (http/https) for this media, or null. Use it as the
   * fallback when a cached blob fails to render on a picky TV decoder.
   */
  originalUrl: string | null;
  /**
   * Report that the current (cached) source failed to render. Drops the blob
   * from the cache and stops caching this URL, so the caller can safely fall
   * back to the network URL and future appearances stream normally.
   */
  reportError: () => void;
}

const isHttpUrl = (url: string | null | undefined): url is string =>
  !!url && /^https?:\/\//i.test(url);

/**
 * Resolve a media URL through the session media cache.
 *
 * - If the file is already cached, returns the in-memory object URL and pins it
 *   for the life of the component so it can't be evicted while on screen.
 * - If it isn't cached yet, returns the original URL for immediate display and
 *   warms the cache in the background, so the NEXT time this media appears it is
 *   served from memory with no network request.
 *
 * The resolved `src` is stable for the life of the mount (keyed on `url`), so the
 * media element never swaps its source mid-display.
 */
export const useMedia = (url: string | null | undefined): UseMediaResult => {
  const reportError = useCallback(() => {
    if (isHttpUrl(url)) {
      mediaCache.markUnusable(url);
    }
  }, [url]);

  const resolved = useMemo<Omit<UseMediaResult, "reportError">>(() => {
    const originalUrl = isHttpUrl(url) ? url : null;

    if (!url) {
      return { status: "idle", src: null, isCached: false, originalUrl: null };
    }

    // Non-network sources (inline data URIs, existing blob URLs) are already
    // local — render them directly, nothing to cache.
    if (!isHttpUrl(url)) {
      return { status: "ready", src: url, isCached: false, originalUrl: null };
    }

    const cached = mediaCache.getObjectUrl(url);
    if (cached) {
      return { status: "ready", src: cached, isCached: true, originalUrl };
    }

    // Not cached yet: show the network URL now; the effect warms the cache.
    return { status: "ready", src: url, isCached: false, originalUrl };
  }, [url]);

  useEffect(() => {
    if (!isHttpUrl(url)) return;

    let pinned = false;
    if (mediaCache.has(url)) {
      // Keep the on-screen file resident until this component unmounts.
      mediaCache.pin(url);
      pinned = true;
    } else {
      // Warm the cache for the next appearance (no-op if already in flight).
      void mediaCache.load(url);
    }

    return () => {
      if (pinned) mediaCache.unpin(url);
    };
  }, [url]);

  return { ...resolved, reportError };
};

/**
 * Eagerly prefetch a list of media URLs into the session cache.
 *
 * Called with every cacheable media URL in a zone's playlist so that all media
 * is downloaded once, up front — after which the display keeps working even if
 * the network becomes slow or unavailable.
 */
export const useMediaPreload = (urls: string[]): void => {
  // Stable dependency so we only re-run when the actual set of URLs changes.
  const key = useMemo(() => urls.slice().sort().join("|"), [urls]);

  useEffect(() => {
    if (urls.length === 0) return;
    void mediaCache.preload(urls);
    // `key` captures the URL set; `urls` identity may change each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};
